"use client";

import * as RadixToast from "@radix-ui/react-toast";
import { AnimatePresence, motion } from "framer-motion";
import {
  ElementRef,
  ReactNode,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useState,
} from "react";
import CloseIcon from "@/components/atoms/svg/icons/CloseIcon";
import InfoIcon from "@/components/atoms/svg/icons/InfoIcon";
import { Button } from "@/components/ui/button";
import { FIVE_SECONDS } from "@/constants/variables";
import { cn } from "@/lib/utils";
import { v4 as uuidV4 } from "uuid";

type ToastPosition =
  | "top-left"
  | "top"
  | "top-right"
  | "bottom-left"
  | "bottom"
  | "bottom-right";

type ToastType = "success" | "info" | "warning" | "error";

interface ToastMessage {
  id: string;
  title?: string;
  text: string;
  type?: ToastType;
  duration?: number;
  position?: ToastPosition;
  onClickbutton1?: () => void;
  onClickbutton2?: () => void;
}

const ToastContext = createContext<{
  toast: (toast: Omit<ToastMessage, "id">) => void;
}>({
  toast: () => {
    throw new Error(
      "You can't call toast() outside of a <ToastProvider> – add it to your tree.",
    );
  },
});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  const idUnique = uuidV4();

  const toast = useCallback(
    ({
      title,
      text,
      type,
      duration = FIVE_SECONDS,
      position = "top-right",
      onClickbutton1,
      onClickbutton2,
    }: Omit<ToastMessage, "id">) => {
      setMessages((toasts) => [
        ...toasts,
        {
          id: idUnique,
          title,
          type,
          text,
          duration,
          position,
          onClickbutton1,
          onClickbutton2,
        },
      ]);
    },
    [idUnique],
  );

  const removeToast = useCallback((id: string) => {
    setMessages((toasts) => toasts.filter((toast) => toast.id !== id));
  }, []);

  return (
    <RadixToast.Provider>
      <ToastContext.Provider value={{ toast }}>
        {children}
      </ToastContext.Provider>

      <AnimatePresence mode="popLayout">
        {messages.map((toast) => (
          <ToastItem
            key={toast.id}
            {...toast}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </AnimatePresence>

      {/* Dynamic Toast Positioning */}
      <RadixToast.Viewport
        className={cn(
          `fixed flex w-auto flex-col-reverse gap-3 z-[999] ${getPositionClass(messages)}`,
        )}
      />
    </RadixToast.Provider>
  );
}

const ToastItem = forwardRef<
  ElementRef<typeof RadixToast.Root>,
  ToastMessage & { onClose: () => void }
>(
  (
    { onClose, title, text, type, duration, onClickbutton1, onClickbutton2 },
    forwardedRef,
  ) => {
    const getType = (type: ToastType) => {
      switch (type) {
        case "success":
          return "#2bc4a1";
        case "warning":
          return " #f0ad3e";
        case "error":
          return " #e6483d";
        default:
          return "#4778F5";
      }
    };

    return (
      <RadixToast.Root
        ref={forwardedRef}
        asChild
        forceMount
        onOpenChange={onClose}
        duration={duration}
      >
        <motion.li
          layout
          initial={{ x: 336 }}
          animate={{ x: 0 }}
          exit={{
            opacity: 0,
            zIndex: -1,
            transition: { opacity: { duration: 0.2 } },
          }}
          transition={{ type: "spring", mass: 1, damping: 30, stiffness: 200 }}
          style={{ width: 400, WebkitTapHighlightColor: "transparent" }}
        >
          <div className="flex gap-3 items-start overflow-hidden p-4 rounded-xl bg-white border border-action-normal shadow-08">
            <InfoIcon
              width={20}
              height={20}
              fill={getType(type || "info")}
              className="mt-0.5"
            />
            <div className="flex flex-col w-full gap-1">
              {title && (
                <div className="flex w-full justify-between">
                  <RadixToast.Title className="font-medium text-base text-base-primary">
                    {title}
                  </RadixToast.Title>
                  <RadixToast.Close>
                    <CloseIcon />
                  </RadixToast.Close>
                </div>
              )}
              {text && (
                <div className="flex w-full justify-between">
                  <RadixToast.Description className="w-full">
                    {text}
                  </RadixToast.Description>
                  {!title && text && (
                    <RadixToast.Close>
                      <CloseIcon />
                    </RadixToast.Close>
                  )}
                </div>
              )}
              {(onClickbutton1 || onClickbutton2) && (
                <div className="flex gap-2 pt-2">
                  {onClickbutton1 && (
                    <Button
                      className="w-[55px] h-6 text-xs"
                      onClick={onClickbutton1}
                    >
                      Button
                    </Button>
                  )}
                  {onClickbutton2 && (
                    <Button
                      variant="outline"
                      className="w-[55px] h-6 text-xs"
                      onClick={onClickbutton2}
                    >
                      Button
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.li>
      </RadixToast.Root>
    );
  },
);

ToastItem.displayName = "ToastItem";

function getPositionClass(messages: ToastMessage[]) {
  if (messages.length === 0) return "top-4 right-4"; // Default to top-right

  const position = messages[messages.length - 1]?.position || "top-right";
  const positions: Record<ToastPosition, string> = {
    "top-left": "top-4 left-4",
    top: "top-4 left-1/2 transform -translate-x-1/2",
    "top-right": "top-4 right-4",
    "bottom-left": "bottom-4 left-4",
    bottom: "bottom-4 left-1/2 transform -translate-x-1/2",
    "bottom-right": "bottom-4 right-4",
  };

  return positions[position] || positions["top-right"];
}
